import React, { useState, useEffect } from 'react';
import {
  Search, MapPin, Calendar, User, Image as ImageIcon,
  CheckCircle, XCircle, Clock, Filter, ChevronRight,
  ExternalLink, Loader2, AlertCircle, Download,
  ThumbsUp, ThumbsDown, BadgeCheck, X, Eye, RefreshCw,
  AlertTriangle, FileImage, Layers, ZoomIn, Trash2
} from 'lucide-react';
import { useLocationGallerySubmissions } from '../../authentication_components/locationGallerySubmissionContext';

// =================================================================
// 1. Image Viewer Modal (Full Screen Image View)
// =================================================================
const ImageViewerModal = ({ imageUrl, onClose }) => {
  if (!imageUrl) return null;

  return (
    <div
      className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
      >
        <X className="h-6 w-6" />
      </button>
      <img
        src={imageUrl}
        alt="Full view"
        referrerPolicy="no-referrer"
        className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

// =================================================================
// 2. Action Dialog (Approve/Reject)
// =================================================================
const ActionDialog = ({ isOpen, onClose, type, image, onConfirm, loading }) => {
  const [verdict, setVerdict] = useState('');

  if (!isOpen) return null;

  const isApprove = type === 'approve';

  const handleConfirm = () => {
    onConfirm(verdict);
    setVerdict('');
  };

  const getHeaderColor = () =>
    isApprove
      ? 'bg-emerald-50 border-emerald-100 text-emerald-800'
      : 'bg-red-50 border-red-100 text-red-800';

  const getIcon = () =>
    isApprove
      ? <ThumbsUp className="h-6 w-6 text-emerald-600" />
      : <ThumbsDown className="h-6 w-6 text-red-600" />;

  const getTitle = () => (isApprove ? 'Aprobar Imagen' : 'Rechazar Imagen');

  const getDescription = () => {
    if (isApprove) {
      return 'Esta imagen será visible públicamente en la galería de la ubicación. ¿Desea agregar un comentario?';
    }
    return 'Por favor, indique la razón del rechazo. Esta nota será enviada al usuario.';
  };

  const getButtonClass = () =>
    isApprove
      ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200'
      : 'bg-red-600 hover:bg-red-700 shadow-red-200';

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
        <div className={`p-6 border-b ${getHeaderColor()}`}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-white/50">{getIcon()}</div>
            <h3 className="text-lg font-bold">{getTitle()}</h3>
          </div>
        </div>

        <div className="p-6">
          {image?.cloudflare_image_url && (
            <div className="mb-4 rounded-lg overflow-hidden border border-slate-200">
              <img
                src={image.cloudflare_image_url}
                alt="Preview"
                referrerPolicy="no-referrer"
                className="w-full h-48 object-cover"
              />
            </div>
          )}

          <p className="text-slate-600 mb-4 text-sm">{getDescription()}</p>

          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            {isApprove ? 'Comentario (Opcional)' : 'Razón del Rechazo (Recomendado)'}
          </label>
          <textarea
            className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-sm"
            rows={3}
            placeholder={isApprove ? "Ej. Excelente calidad..." : "Ej. Imagen borrosa, contenido inapropiado..."}
            value={verdict}
            onChange={(e) => setVerdict(e.target.value)}
          />

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading}
              className={`flex-1 px-4 py-2.5 text-white font-medium rounded-xl shadow-md transition-colors flex items-center justify-center disabled:opacity-50 ${getButtonClass()}`}
            >
              {loading ? <Loader2 className="animate-spin h-5 w-5" /> : 'Confirmar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =================================================================
// 3. Delete Confirmation Dialog
// =================================================================
const DeleteConfirmDialog = ({ isOpen, onClose, image, onConfirm, loading }) => {
  const [confirmText, setConfirmText] = useState('');

  if (!isOpen) return null;

  const canConfirm = confirmText.trim().toUpperCase() === 'ELIMINAR';

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm();
    setConfirmText('');
  };

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-6 border-b bg-red-50 border-red-100 text-red-800">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-white/50">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold">Eliminar Imagen Permanentemente</h3>
          </div>
        </div>

        <div className="p-6">
          {image?.cloudflare_image_url && (
            <div className="mb-4 rounded-lg overflow-hidden border border-slate-200">
              <img
                src={image.cloudflare_image_url}
                alt="Preview"
                referrerPolicy="no-referrer"
                className="w-full h-40 object-cover"
              />
            </div>
          )}

          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">
              Esta acción es <strong>permanente</strong>. La imagen #{image?.id} será eliminada de la galería y no podrá recuperarse.
            </p>
          </div>

          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            Para confirmar, escriba <span className="text-red-600">ELIMINAR</span>
          </label>
          <input
            type="text"
            className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm"
            placeholder="ELIMINAR"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoFocus
          />

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading || !canConfirm}
              className="flex-1 px-4 py-2.5 text-white font-medium rounded-xl shadow-md transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed bg-red-600 hover:bg-red-700 shadow-red-200"
            >
              {loading ? (
                <Loader2 className="animate-spin h-5 w-5" />
              ) : (
                <><Trash2 className="h-4 w-4 mr-2" /> Eliminar</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =================================================================
// 4. Image Details Modal
// =================================================================
const ImageDetailModal = ({ image, onClose, onAction }) => {
  const [viewerImage, setViewerImage] = useState(null);

  if (!image) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-PR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusStyle = (status) => {
    if (status === 'approved') return 'bg-emerald-50 border-emerald-100 text-emerald-800';
    if (status === 'rejected') return 'bg-red-50 border-red-100 text-red-800';
    return 'bg-amber-50 border-amber-100 text-amber-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'approved') return <CheckCircle className="h-5 w-5" />;
    if (status === 'rejected') return <XCircle className="h-5 w-5" />;
    return <Clock className="h-5 w-5" />;
  };

  const getStatusText = (status) => {
    if (status === 'approved') return 'Aprobado';
    if (status === 'rejected') return 'Rechazado';
    return 'Pendiente';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col relative" onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div className="p-6 border-b border-slate-100 flex justify-between items-start bg-white">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold text-violet-500 bg-violet-50 px-2 py-0.5 rounded-full uppercase tracking-wide">
                  Imagen #{image.id}
                </span>
                {image.upload_source && (
                  <span className="text-xs text-slate-500 bg-slate-50 px-2 py-0.5 rounded-full">
                    {image.upload_source === 'user_contribution' ? '👤 Usuario' :
                      image.upload_source === 'business_gallery' ? '🏢 Negocio' :
                      '⚙️ Admin'}
                  </span>
                )}
              </div>
              <h2 className="text-2xl font-bold text-slate-800">{image.location_name || 'Ubicación sin nombre'}</h2>
              <div className="flex items-center text-slate-500 text-sm mt-1">
                <MapPin className="h-3.5 w-3.5 mr-1" /> {image.location_id}
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-full transition-colors">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid md:grid-cols-2 gap-6">

              {/* Left: Image */}
              <div>
                <h4 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                  <ImageIcon className="h-5 w-5 text-violet-500" /> Vista Previa
                </h4>
                <div className="relative rounded-xl overflow-hidden border border-slate-200 shadow-md bg-slate-100 group">
                  <img
                    src={image.cloudflare_image_url}
                    alt="Image preview"
                    referrerPolicy="no-referrer"
                    className="w-full h-96 object-cover cursor-pointer"
                    onClick={() => setViewerImage(image.cloudflare_image_url)}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/400x300?text=Image+Error';
                    }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="bg-white/90 rounded-full p-3">
                      <ZoomIn className="h-6 w-6 text-slate-700" />
                    </div>
                  </div>
                </div>

                {/* Image Metadata */}
                <div className="mt-4 bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Tamaño:</span>
                    <span className="font-medium text-slate-700">{formatFileSize(image.file_size)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Tipo:</span>
                    <span className="font-medium text-slate-700">{image.content_type || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Archivo:</span>
                    <span className="font-medium text-slate-700 truncate max-w-[200px]" title={image.original_filename}>
                      {image.original_filename || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Cloudflare ID:</span>
                    <span className="font-mono text-xs text-slate-600 truncate max-w-[200px]" title={image.cloudflare_image_id}>
                      {image.cloudflare_image_id}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right: Details */}
              <div className="space-y-4">

                {/* User Info */}
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                  <h5 className="text-xs font-bold text-slate-400 uppercase mb-3">Información del Usuario</h5>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-10 w-10 rounded-full bg-violet-100 flex items-center justify-center text-violet-600">
                      <User className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-700">
                          {image.user_first_name || image.user_last_name
                            ? `${image.user_first_name || ''} ${image.user_last_name || ''}`.trim()
                            : 'Usuario Anónimo'}
                        </span>
                      </div>
                      {image.user_id && (
                        <p className="text-xs text-slate-500 font-mono">ID: {image.user_id}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Calendar className="h-3 w-3" />
                    <span>Subida: {formatDate(image.created_at)}</span>
                  </div>
                </div>

                {/* Status */}
                {image.image_status && (
                  <div className={`p-4 rounded-xl border ${getStatusStyle(image.image_status)}`}>
                    <h5 className="font-bold flex items-center gap-2 mb-2">
                      {getStatusIcon(image.image_status)}
                      Estado: {getStatusText(image.image_status)}
                    </h5>
                    {image.image_verdict && (
                      <p className="text-sm mt-2 italic">"{image.image_verdict}"</p>
                    )}
                    {image.admin_id && (
                      <p className="text-xs mt-2 text-slate-600">
                        Revisado por: <span className="font-mono">{image.admin_id}</span>
                      </p>
                    )}
                  </div>
                )}

                {/* Image Stats */}
                <div className="bg-white border border-slate-200 p-4 rounded-xl">
                  <h5 className="text-xs font-bold text-slate-400 uppercase mb-3">Estadísticas</h5>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-slate-500">Me Gusta</p>
                      <p className="text-lg font-bold text-slate-700">{image.image_likes || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Orden</p>
                      <p className="text-lg font-bold text-slate-700">{image.image_order !== null ? image.image_order : 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h5 className="text-xs font-bold text-slate-400 uppercase mb-3">Enlaces</h5>
                  <a
                    href={image.cloudflare_image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-700 font-medium"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Ver Imagen Original
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          {image.image_status === 'pending' && (
            <div className="p-6 border-t border-slate-100 bg-slate-50 grid grid-cols-2 gap-3">
              <button
                onClick={() => onAction('reject', image)}
                className="py-3 px-4 bg-white border border-red-200 text-red-600 rounded-xl font-bold hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
              >
                <XCircle className="h-5 w-5" /> Rechazar
              </button>
              <button
                onClick={() => onAction('approve', image)}
                className="py-3 px-4 bg-violet-600 text-white rounded-xl font-bold hover:bg-violet-700 shadow-lg shadow-violet-200 transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle className="h-5 w-5" /> Aprobar
              </button>
            </div>
          )}

          {image.image_status === 'approved' && (
            <div className="p-6 border-t border-slate-100 bg-slate-50">
              <button
                onClick={() => onAction('delete', image)}
                className="w-full py-3 px-4 bg-white border border-red-200 text-red-600 rounded-xl font-bold hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 className="h-5 w-5" /> Eliminar Imagen
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Image Viewer */}
      <ImageViewerModal
        imageUrl={viewerImage}
        onClose={() => setViewerImage(null)}
      />
    </>
  );
};

// =================================================================
// 5. Error Alert Component
// =================================================================
const ErrorAlert = ({ message, onDismiss }) => {
  if (!message) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-bold text-red-800 text-sm">Error</h4>
          <p className="text-red-600 text-sm mt-1">{message}</p>
        </div>
        <button onClick={onDismiss} className="text-red-400 hover:text-red-600">
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

// =================================================================
// 6. Success Toast Component
// =================================================================
const SuccessToast = ({ message, onDismiss }) => {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        onDismiss();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onDismiss]);

  if (!message) return null;

  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 duration-300">
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3 shadow-lg">
        <CheckCircle className="h-5 w-5 text-emerald-600" />
        <p className="text-emerald-800 font-medium text-sm">{message}</p>
        <button onClick={onDismiss} className="text-emerald-400 hover:text-emerald-600">
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

// =================================================================
// 7. MAIN COMPONENT
// =================================================================
export default function LocationGallerySubmissions() {
  // Context
  const {
    loading: contextLoading,
    error: contextError,
    getImages,
    getPendingImages,
    getApprovedImages,
    getRejectedImages,
    approveImage,
    rejectImage,
    deleteImage,
    clearError,
  } = useLocationGallerySubmissions();

  // Local State
  const [images, setImages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [selectedImage, setSelectedImage] = useState(null);
  const [actionDialog, setActionDialog] = useState({ isOpen: false, type: null, image: null });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, image: null });
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [localError, setLocalError] = useState('');

  // Fetch images on mount and when filter changes
  useEffect(() => {
    fetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const fetchImages = async () => {
    try {
      setLocalError('');
      let result;

      switch (filterStatus) {
        case 'pending':
          result = await getPendingImages({ limit: 100 });
          break;
        case 'approved':
          result = await getApprovedImages({ limit: 100 });
          break;
        case 'rejected':
          result = await getRejectedImages({ limit: 100 });
          break;
        default:
          result = await getImages({ limit: 100 });
      }

      setImages(result.images || []);
    } catch (err) {
      console.error('[LocationGallerySubmissions] Error fetching images:', err);
      setLocalError(err.message || 'Error al cargar las imágenes');
    }
  };

  // Filter images locally by search term
  const filteredImages = images.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (item.location_name?.toLowerCase().includes(searchLower)) ||
      (item.location_id?.toLowerCase().includes(searchLower)) ||
      (item.cloudflare_image_id?.toLowerCase().includes(searchLower)) ||
      (item.user_first_name?.toLowerCase().includes(searchLower)) ||
      (item.user_last_name?.toLowerCase().includes(searchLower))
    );
  });

  // Handlers
  const handleOpenAction = (type, image) => {
    setActionDialog({ isOpen: true, type, image });
  };

  const handleOpenDelete = (image) => {
    setDeleteDialog({ isOpen: true, image });
  };

  const handleConfirmAction = async (verdict) => {
    const { type, image } = actionDialog;
    if (!image) return;

    setProcessing(true);
    setLocalError('');

    try {
      if (type === 'approve') {
        await approveImage(image.id, verdict || null);
        setSuccessMessage(`Imagen #${image.id} aprobada exitosamente.`);
      } else {
        await rejectImage(image.id, verdict || null);
        setSuccessMessage(`Imagen #${image.id} rechazada.`);
      }

      await fetchImages();
      setActionDialog({ isOpen: false, type: null, image: null });
      setSelectedImage(null);
    } catch (err) {
      console.error('Error processing action:', err);
      setLocalError(err.message || 'Error al procesar la acción');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmDelete = async () => {
    const { image } = deleteDialog;
    if (!image) return;

    setProcessing(true);
    setLocalError('');

    try {
      await deleteImage(image.id);
      setSuccessMessage(`Imagen #${image.id} eliminada exitosamente.`);
      await fetchImages();
      setDeleteDialog({ isOpen: false, image: null });
      setSelectedImage(null);
    } catch (err) {
      console.error('Error deleting image:', err);
      setLocalError(err.message || 'Error al eliminar la imagen');
    } finally {
      setProcessing(false);
    }
  };

  const handleRefresh = () => {
    fetchImages();
  };

  // Detail modal action router — handles approve/reject/delete from inside the modal
  const handleDetailModalAction = (type, image) => {
    if (type === 'delete') {
      handleOpenDelete(image);
    } else {
      handleOpenAction(type, image);
    }
  };

  // Render Status Badge
  const StatusBadge = ({ status }) => {
    const styles = {
      pending: 'bg-amber-50 text-amber-700 border-amber-100',
      approved: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      rejected: 'bg-red-50 text-red-700 border-red-100',
    };

    const icons = {
      pending: Clock,
      approved: CheckCircle,
      rejected: XCircle,
    };

    const Icon = icons[status] || Clock;
    const statusText = status === 'pending' ? 'Pendiente' :
      status === 'approved' ? 'Aprobado' :
        status === 'rejected' ? 'Rechazado' : status;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
        <Icon className="h-3 w-3 mr-1" />
        {statusText}
      </span>
    );
  };

  const displayError = localError || contextError;

  return (
    <div className="min-h-screen bg-slate-50/50">

      {/* Success Toast */}
      <SuccessToast message={successMessage} onDismiss={() => setSuccessMessage('')} />

      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Galería de Imágenes</h1>
              <p className="text-sm text-slate-500">Revisión de imágenes enviadas por usuarios para ubicaciones.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={contextLoading}
                className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-50"
                title="Actualizar"
              >
                <RefreshCw className={`h-5 w-5 ${contextLoading ? 'animate-spin' : ''}`} />
              </button>
              <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
                {['pending', 'approved', 'rejected'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilterStatus(status)}
                    disabled={contextLoading}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap disabled:opacity-50 ${
                      filterStatus === status
                        ? 'bg-white text-violet-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {status === 'pending' ? 'Pendientes' :
                      status === 'approved' ? 'Aprobadas' :
                        'Rechazadas'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por ubicación, nombre de usuario o ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
            />
          </div>
        </div>
      </div>

      {/* Error Alert */}
      <ErrorAlert message={displayError} onDismiss={() => { clearError(); setLocalError(''); }} />

      {/* Grid Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {contextLoading ? (
          <div className="flex flex-col items-center justify-center h-64">
            <Loader2 className="h-8 w-8 text-violet-500 animate-spin mb-2" />
            <p className="text-slate-400 text-sm">Cargando imágenes...</p>
          </div>
        ) : filteredImages.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col items-center justify-center h-64 text-center px-4">
            <div className="bg-slate-50 p-3 rounded-full mb-3">
              <FileImage className="h-6 w-6 text-slate-300" />
            </div>
            <p className="text-slate-500 font-medium">
              {searchTerm ? 'No se encontraron resultados para tu búsqueda.' : 'No hay imágenes en esta categoría.'}
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="mt-2 text-sm text-violet-600 hover:text-violet-700"
              >
                Limpiar búsqueda
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredImages.map((image) => (
                <div
                  key={image.id}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => setSelectedImage(image)}
                >
                  {/* Image */}
                  <div className="relative h-48 bg-slate-100">
                    <img
                      src={image.cloudflare_image_url}
                      alt={image.location_name || 'Image'}
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/400x300?text=Image+Error';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute top-2 right-2">
                      <StatusBadge status={image.image_status} />
                    </div>

                    {image.image_status === 'pending' && (
                      <div className="absolute bottom-2 left-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenAction('reject', image);
                          }}
                          className="flex-1 py-1.5 px-2 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700 transition-colors"
                        >
                          Rechazar
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenAction('approve', image);
                          }}
                          className="flex-1 py-1.5 px-2 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 transition-colors"
                        >
                          Aprobar
                        </button>
                      </div>
                    )}

                    {image.image_status === 'approved' && (
                      <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDelete(image);
                          }}
                          className="p-2 bg-red-600 text-white rounded-lg shadow-lg hover:bg-red-700 transition-colors"
                          title="Eliminar imagen"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-bold text-slate-800 text-sm mb-1 truncate">
                      {image.location_name || 'Sin nombre'}
                    </h3>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                      <MapPin className="h-3 w-3" />
                      {image.location_id}
                    </p>

                    <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-100">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {image.user_first_name || 'Anónimo'}
                      </span>
                      <span>
                        {image.created_at ? new Date(image.created_at).toLocaleDateString('es-PR') : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Results Summary */}
            <div className="mt-6 text-center text-sm text-slate-500">
              Mostrando {filteredImages.length} {filteredImages.length === 1 ? 'imagen' : 'imágenes'}
              {searchTerm && ` para "${searchTerm}"`}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      <ImageDetailModal
        image={selectedImage}
        onClose={() => setSelectedImage(null)}
        onAction={handleDetailModalAction}
      />

      <ActionDialog
        isOpen={actionDialog.isOpen}
        type={actionDialog.type}
        image={actionDialog.image}
        loading={processing}
        onClose={() => setActionDialog({ isOpen: false, type: null, image: null })}
        onConfirm={handleConfirmAction}
      />

      <DeleteConfirmDialog
        isOpen={deleteDialog.isOpen}
        image={deleteDialog.image}
        loading={processing}
        onClose={() => setDeleteDialog({ isOpen: false, image: null })}
        onConfirm={handleConfirmDelete}
      />

    </div>
  );
}